// Legacy DashboardComponent parity: WeeklyChart (per-type weekly totals with sparkline),
// month/year selectors, daily overview (line, 4 series) and monthly overview (column, 4 series).

import { Box, Card, CardContent, FormControl, InputLabel, LinearProgress, MenuItem, Select, Typography } from '@mui/material';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';

import { ACCENT } from '../../brand';
import { getLocale, t } from '../../i18n';
import type { DashboardTimeSeries } from '../../types';
import { dashboardYears } from '../usePeriodCharts';
import { UI } from '../../brand/ui';

const baseFont = "'Inter', 'Sohne', Arial, sans-serif";
const TYPES = ['sale', 'purchase', 'income', 'expense'] as const;
const COLORS: Record<(typeof TYPES)[number], string> = { sale: ACCENT[600], purchase: UI.navy, income: UI.muted, expense: UI.warning };

function sortedEntries(obj: Record<string, number> | undefined) {
  return Object.entries(obj ?? {})
    .filter(([k]) => k)
    .sort((a, b) => a[0].localeCompare(b[0]));
}

function total(obj: Record<string, number> | undefined) {
  return Object.values(obj ?? {}).reduce((acc, v) => acc + (Number(v) || 0), 0);
}

const compact = (val: number) => (Math.abs(val) >= 1000 ? `${(val / 1000).toFixed(0)}k` : String(Math.round(val)));

export function WeeklyTotals({ week }: { week?: DashboardTimeSeries }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
      {TYPES.map((type) => {
        const entries = sortedEntries(week?.[type]);
        const options: ApexOptions = {
          chart: { type: 'area', sparkline: { enabled: true }, fontFamily: baseFont },
          colors: [COLORS[type]],
          stroke: { curve: 'smooth', width: 2 },
          fill: { type: 'solid', opacity: 0.12 },
          xaxis: { categories: entries.map(([d]) => new Date(`${d}T00:00:00`).toLocaleDateString(getLocale(), { weekday: 'short', day: 'numeric' })) },
          tooltip: { theme: 'light', y: { formatter: (v) => v.toLocaleString(undefined, { maximumFractionDigits: 0 }) } }
        };
        return (
          <Card key={type}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 1 } }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {total(week?.[type]).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(`dashboard.weekTotal.${type}`)}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chart options={options} series={[{ name: t(`dashboard.series.${type}`), data: entries.map(([, v]) => Math.round(Number(v) || 0)) }]} type="area" height={56} />
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}

export function PeriodSelectors({ month, year, onMonth, onYear, loading }: { month: number; year: number; onMonth: (m: number) => void; onYear: (y: number) => void; loading?: boolean }) {
  const months = Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleDateString(getLocale(), { month: 'long' }));
  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <FormControl size="small" fullWidth>
          <InputLabel>{t('dashboard.month')}</InputLabel>
          <Select label={t('dashboard.month')} value={month} onChange={(e) => onMonth(Number(e.target.value))}>
            {months.map((label, i) => (
              <MenuItem key={label} value={i + 1}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>{t('dashboard.year')}</InputLabel>
          <Select label={t('dashboard.year')} value={year} onChange={(e) => onYear(Number(e.target.value))}>
            {dashboardYears().map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {loading ? <LinearProgress sx={{ mt: 1 }} /> : <Box sx={{ height: 4, mt: 1 }} />}
    </Box>
  );
}

function overviewOptions(type: 'line' | 'bar', categories: string[]): ApexOptions {
  return {
    chart: { type, height: 300, toolbar: { show: false }, fontFamily: baseFont },
    colors: TYPES.map((k) => COLORS[k]),
    stroke: type === 'line' ? { curve: 'smooth', width: 2 } : { width: 0 },
    plotOptions: { bar: { borderRadius: 3, columnWidth: '70%' } },
    dataLabels: { enabled: false },
    grid: { borderColor: UI.border, strokeDashArray: 3, padding: { left: 4, right: 4 } },
    xaxis: { categories, labels: { style: { fontSize: '11px', colors: UI.textSecondary } }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { min: 0, labels: { style: { fontSize: '11px', colors: UI.textSecondary }, formatter: compact } },
    tooltip: { theme: 'light', shared: true, intersect: false, y: { formatter: (v) => v.toLocaleString(undefined, { maximumFractionDigits: 0 }) } },
    legend: { show: true, position: 'top', horizontalAlign: 'right', fontSize: '12px', markers: { size: 6, shape: 'circle' as const } }
  };
}

function series(data: DashboardTimeSeries | undefined, keys: string[]) {
  return TYPES.map((type) => ({ name: t(`dashboard.series.${type}`), data: keys.map((k) => Math.round(Number(data?.[type]?.[k]) || 0)) }));
}

function allKeys(data: DashboardTimeSeries | undefined) {
  const keys = new Set<string>();
  TYPES.forEach((type) => Object.keys(data?.[type] ?? {}).forEach((k) => k && keys.add(k)));
  return Array.from(keys).sort();
}

function Empty() {
  return (
    <Box sx={{ height: 240, display: 'grid', placeItems: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {t('common.notEnoughData')}
      </Typography>
    </Box>
  );
}

/** Daily overview for the selected month (legacy "daily_overview_chart", line). */
export function DailyOverviewChart({ data }: { data?: DashboardTimeSeries }) {
  const keys = allKeys(data);
  if (!keys.length) return <Empty />;
  const categories = keys.map((k) => String(new Date(`${k}T00:00:00`).getDate()));
  return <Chart options={overviewOptions('line', categories)} series={series(data, keys)} type="line" height={300} />;
}

/** Monthly overview for the selected year (legacy "monthly_overview_chart", column). */
export function MonthlyOverviewChart({ data }: { data?: DashboardTimeSeries }) {
  const keys = allKeys(data);
  if (!keys.length) return <Empty />;
  const categories = keys.map((k) => new Date(`${k}-01T00:00:00`).toLocaleDateString(getLocale(), { month: 'short' }));
  return <Chart options={overviewOptions('bar', categories)} series={series(data, keys)} type="bar" height={300} />;
}
